// BackgroundJobsTab.tsx — self-service, read-only view of this hospital's
// own background-job run history (backend/jobs/, ledgered in
// platform.job_runs). See docs/HMS_V4_BACKGROUND_JOBS.md for what a
// background job is and why this ledger exists; this tab is purely a window
// onto it, no controls that change anything.
import { useState } from 'react';
import { Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { NativeSelect } from '@/components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useJobRuns, useJobDefinitions } from './useJobRuns';

const dateTime = (v: string) => new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });

function durationLabel(startedAt: string, finishedAt: string): string {
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function BackgroundJobsTab() {
  const [jobName, setJobName] = useState('');
  const [status, setStatus] = useState('');

  const definitions = useJobDefinitions();
  const runs = useJobRuns({
    job_name: jobName || undefined,
    status: (status || undefined) as 'ok' | 'error' | undefined,
    limit: 100,
  });

  return (
    <div>
      <div className="mb-4 text-[13px] leading-relaxed text-ink-muted">
        Scheduled maintenance that runs quietly in the background for this hospital — no one clicks
        a button to trigger it, so this is where you can check it's actually happening. Each row is
        one attempt; a job with no recent rows here, or with several rows in a row failing, is worth
        flagging. This view is read-only.
      </div>

      <Card>
        <CardHeader>
          <CardTitle><Clock size={15} /> Job run history</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <NativeSelect
              className="h-8 w-auto text-[12px]"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
            >
              <option value="">All jobs</option>
              {(definitions.data ?? []).map((d) => (
                <option key={d.name} value={d.name}>{d.name}</option>
              ))}
            </NativeSelect>
            <NativeSelect
              className="h-8 w-auto text-[12px]"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Any status</option>
              <option value="ok">OK</option>
              <option value="error">Error</option>
            </NativeSelect>
          </div>
        </CardHeader>

        {runs.isError ? (
          <ErrorState
            message={runs.error instanceof ApiError ? runs.error.message : 'Could not load job run history'}
            onRetry={() => runs.refetch()}
          />
        ) : runs.isLoading ? (
          <LoadingState label="Loading job run history…" />
        ) : (runs.data ?? []).length === 0 ? (
          <EmptyState
            label="No job runs match this filter"
            hint={jobName || status ? 'Try clearing a filter.' : 'This hospital has no recorded job runs yet.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Items processed</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(runs.data ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-[12px] font-medium">{r.job_name}</TableCell>
                    <TableCell>
                      <Badge variant={r.status === 'ok' ? 'success' : 'danger'}>{r.status === 'ok' ? 'OK' : 'Error'}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.items_processed ?? '—'}</TableCell>
                    <TableCell className="text-[12px] text-ink-muted">{dateTime(r.started_at)}</TableCell>
                    <TableCell className="text-[12px] tabular-nums text-ink-muted">{durationLabel(r.started_at, r.finished_at)}</TableCell>
                    <TableCell className="max-w-[280px] truncate text-[12px] text-danger-ink" title={r.error ?? undefined}>
                      {r.error ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
